import React, { useState, useEffect, useCallback } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    FormFeedback
} from "reactstrap";
import { GetBankDetails, AddBankDetails, UpdateBankDetails } from "../../../../api-handle/financeApi";
import Loader from "../../../../CommonElements/Loader/loader";
import { debounce } from "lodash";

// Utility functions
const isValidKuwaitIBAN = (iban) => {
    iban = iban.replace(/\s/g, "").toUpperCase();
    if (!/^KW\d{2}[A-Z]{4}[A-Z0-9]{22}$/.test(iban)) return false;
    if (iban.length !== 30) return false;
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const numeric = rearranged.replace(/[A-Z]/g, (letter) =>
        (letter.charCodeAt(0) - 55).toString()
    );
    let remainder = numeric
        .split("")
        .reduce((acc, digit) => (acc * 10 + parseInt(digit)) % 97, 0);
    return remainder === 1;
};

const formatKuwaitIBAN = (iban) => {
    iban = iban.replace(/\s/g, "").toUpperCase();
    if (iban.length !== 30) return iban;
    return iban.match(/.{1,4}/g)?.join(" ") || iban;
};

const BankDetails = () => {
    const [bankDetails, setBankDetails] = useState(null);
    const [formData, setFormData] = useState({ iban: "", bankName: "" });
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [ibanValid, setIbanValid] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const validateIbanDebounced = useCallback(
        debounce((value) => {
            setIbanValid(value ? isValidKuwaitIBAN(value.replace(/\s/g, "")) : null);
        }, 300),
        []
    );

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await GetBankDetails();
            if (res?.isSuccess && res.bankDetails?._id) {
                setBankDetails(res.bankDetails);
                setFormData({
                    iban: formatKuwaitIBAN(res.bankDetails.iban),
                    bankName: res.bankDetails.bankName || "",
                });
                setIbanValid(isValidKuwaitIBAN(res.bankDetails.iban));
            }
        } catch (err) {
            setError("Failed to fetch bank details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "iban") {
            const formatted = formatKuwaitIBAN(value);
            setFormData((prev) => ({ ...prev, iban: formatted }));
            validateIbanDebounced(formatted);
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        setError("");
        setSuccess("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const cleanIban = formData.iban.replace(/\s/g, "").toUpperCase();
        if (!isValidKuwaitIBAN(cleanIban)) {
            setError("Invalid Kuwait IBAN format.");
            return;
        }

        if (!formData.bankName.trim()) {
            setError("Bank name is required.");
            return;
        }

        setLoading(true);
        try {
            const payload = { iban: cleanIban, bankName: formData.bankName.trim() };
            let res;

            if (bankDetails?._id) {
                res = await UpdateBankDetails(payload);
            } else {
                res = await AddBankDetails(payload);
            }

            if (res.isSuccess) {
                setSuccess(res.message || "Bank details saved.");
                setEditing(false);
                await fetchDetails();
            } else {
                setError(res.message || "Something went wrong.");
            }
        } catch (err) {
            setError(err.message || "Server error.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        if (bankDetails) {
            setFormData({
                iban: formatKuwaitIBAN(bankDetails.iban),
                bankName: bankDetails.bankName || "",
            });
        } else {
            setFormData({ iban: "", bankName: "" });
        }
        setEditing(false);
        setError("");
        setSuccess("");
    };

    return (
        <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Bank Details</h5>
                {!editing && (
                    <Button color="primary" size="sm" onClick={() => setEditing(true)}>
                        {bankDetails ? "Edit" : "Add"} Details
                    </Button>
                )}
            </CardHeader>
            <CardBody>
                {loading && <Loader />}

                {error && <Alert color="danger">{error}</Alert>}
                {success && <Alert color="success">{success}</Alert>}

                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label>IBAN</Label>
                                <Input
                                    name="iban"
                                    value={formData.iban}
                                    onChange={handleInputChange}
                                    disabled={!editing}
                                    invalid={ibanValid === false}
                                    valid={ibanValid === true}
                                    placeholder="KW81 XXXX XXXX XXXX XXXX XXXX XXXX"
                                    autoComplete="off"
                                />
                                <FormFeedback valid>Valid Kuwait IBAN</FormFeedback>
                                <FormFeedback>Invalid IBAN format</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label>Bank Name</Label>
                                <Input
                                    name="bankName"
                                    value={formData.bankName}
                                    onChange={handleInputChange}
                                    disabled={!editing}
                                    invalid={editing && !formData.bankName.trim()}
                                    autoComplete="off"
                                />
                                <FormFeedback>Bank name is required</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    {editing && (
                        <div className="d-flex gap-2 mt-3">
                            <Button
                                color="success"
                                type="submit"
                                disabled={loading || !ibanValid || !formData.bankName.trim()}
                            >
                                {bankDetails ? "Update" : "Save"}
                            </Button>
                            <Button type="button" color="secondary" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </div>
                    )}
                </form>
            </CardBody>
        </Card>
    );
};

export default BankDetails;
